import * as React from 'react';
import type { strokeDashTypes } from 'react-financial-charts';
import { MouseLocationIndicator } from '@react-financial-charts/interactive/lib/components/index';
import { isHoverForInteractiveType, saveNodeType } from '@react-financial-charts/interactive/lib/utils';
import { BrushShape } from './Wrapper/Brush';
import { ClickableArrowHead, InteractiveText } from './Wrapper';
import { MeasureBgColor } from '@/utils/findMatchingShape';
import { solidColor } from '@/utils/rgbTorgba';
import type { ChartId, MeasureSharedState, MoreProps } from '../../../types/commontypes';
import { ShapeType } from '../../../types/commontypes';
import { ChartShapesContext } from '../../../contexts/ChartShapesContext';
import { computeMeasureMetrics, type MeasureMetrics } from '@/utils/measureMetrics';

export interface MeasureProps {
  readonly enabled: boolean;
  readonly onBrush: (e: React.MouseEvent, rect: unknown, moreProps: MoreProps) => void;
  readonly onStart: (e: React.MouseEvent, position: unknown, moreProps: MoreProps) => void;
  readonly completedRect: Record<string, unknown>[] | null;
  readonly apperance: {
    readonly strokeStyle: string;
    readonly fillStyle?: string;
    readonly strokeDashArray?: strokeDashTypes;
    readonly bgFill: string;
    readonly textFill: string;
    readonly fontFamily: string;
    readonly fontWeight: string;
    readonly fontStyle: string;
    readonly fontSize: number;
  };
  readonly currentPositionStroke?: string;
  readonly currentPositionStrokeWidth?: number;
  readonly currentPositionstrokeOpacity?: number;
  readonly currentPositionRadius?: number;
  readonly chartId: ChartId;
  readonly timeFrame?: string;
  readonly sharedState?: MeasureSharedState;
  readonly setSharedState?: (
    sharedState: MeasureSharedState | ((prev: MeasureSharedState) => MeasureSharedState),
  ) => void;
}

interface MeasureState {
  x1y1?: number[] | null;
  start?: { item: unknown; xValue: number; yValue: number };
  rect?: { x: number; y: number; width: number; height: number } | null;
  end?: { xValue: number; yValue: number };
  /** Derived render data computed via computeMeasureMetrics */
  metrics?: MeasureMetrics | null;
}

export class Measure extends React.Component<MeasureProps, MeasureState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  public static defaultProps = {
    currentPositionStroke: '',
    currentPositionstrokeOpacity: 1,
    currentPositionStrokeWidth: 1,
    currentPositionRadius: 0,
    apperance: {
      fillStyle: 'rgba(70, 41, 156, 0.55)',
    },
  };

  private mouseMoved: boolean = false;

  private getSelectionState: (...args: unknown[]) => unknown;
  private saveNodeType: <T>(idx: number) => React.Ref<T>;
  private zoomHappening?: boolean;

  public constructor(props: MeasureProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType('measure').bind(this);

    this.state = { rect: null };
  }

  public terminate() {
    this.zoomHappening = false;
    this.setState({
      x1y1: null,
      start: undefined,
      end: undefined,
      rect: null,
      metrics: null,
    });
  }

  public render() {
    const {
      enabled,
      currentPositionstrokeOpacity,
      currentPositionRadius = Measure.defaultProps.currentPositionRadius,
      currentPositionStrokeWidth,
      sharedState,
      chartId,
    } = this.props;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const typedSharedState = (sharedState ?? {}) as MeasureSharedState;
    const { startPosition, end } = typedSharedState;
    const { completedRect, apperance } = this.props;
    const { strokeDashArray, strokeStyle, fillStyle, fontFamily, fontSize, textFill, fontStyle, fontWeight } =
      apperance;

    // Read derived rendering data from local state (computed via computeMeasureMetrics)
    const metrics = this.state.metrics;
    const drawingRect = metrics?.rect ?? null;

    const tempCondition =
      drawingRect && chartId === typedSharedState.chartId && typedSharedState.status !== 'completed';
    const fillBgColor =
      chartId === typedSharedState.chartId
        ? startPosition && end && MeasureBgColor({ start: startPosition, end })
        : fillStyle;
    const solidStrockeStyle = strokeStyle ? strokeStyle : solidColor(fillBgColor!);
    const renderArrowHead = (
      startXY: [number, number] | undefined,
      endXY: [number, number] | undefined,
      direction: string | undefined,
    ) => {
      if (!startXY || !endXY || !direction) return null;
      return (
        <ClickableArrowHead
          startXY={startXY}
          endXY={endXY}
          strokeWidth={1}
          strokeStyle={solidStrockeStyle}
          fillStyle={strokeStyle}
          r={5}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          arrowDirection={direction as 'left' | 'right' | 'both' | 'none'}
          show={true}
          usePixelCoordinates={true}
        />
      );
    };
    const temp =
      tempCondition && drawingRect ? (
        <BrushShape
          rect={drawingRect}
          strokeStyle={solidStrockeStyle}
          strokeDashArray={strokeDashArray}
          fillStyle={fillBgColor ?? undefined}
        />
      ) : null;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const completedRectShape = completedRect?.[0]?.rect as
      | { x: number; y: number; width: number; height: number }
      | undefined;

    // Build render items: active drawing metrics + completed shape data
    const drawingData =
      metrics && chartId === typedSharedState.chartId && typedSharedState.status !== 'completed' ? metrics : null;

    const completedData = completedRect?.[0];

    return (
      <g>
        {/* Render completed shape */}
        {completedRectShape && (
          <BrushShape
            rect={completedRectShape}
            strokeStyle={strokeStyle}
            strokeDashArray={strokeDashArray}
            fillStyle={fillStyle}
          />
        )}

        {/* Render shape while drawing */}
        {temp}

        {/* Render shape text, arrow (during drawing or after completion) */}
        {chartId === typedSharedState.chartId &&
          [drawingData, completedData].map((data, i) => {
            if (!data) return null;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const textLines = (data.textLines ?? []) as string[];
            const dataRect = data.rect;
            if (!dataRect) return null;
            const text = textLines.join('\n');
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const dataTextPosition = (data.textPosition ?? { xValue: 0, yValue: 0 }) as {
              xValue: number;
              yValue: number;
            };
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const dataVerticalStartXY = data.verticalStartXY as [number, number] | undefined;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const dataVerticalEndXY = data.verticalEndXY as [number, number] | undefined;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const dataHorizontalStartXY = data.horizontalStartXY as [number, number] | undefined;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const dataHorizontalEndXY = data.horizontalEndXY as [number, number] | undefined;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const dataArrowDirectionVert = data.arrowDirectionVert as string | undefined;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const dataArrowDirectionHorz = data.arrowDirectionHorz as string | undefined;

            return textLines.map((_line: string, idx: number) => (
              <g key={idx}>
                <InteractiveText
                  key={`interactive-text-${i}`}
                  position={[dataTextPosition.xValue, dataTextPosition.yValue]}
                  text={text}
                  fontFamily={fontFamily}
                  fontSize={fontSize}
                  fontStyle={fontStyle}
                  fontWeight={fontWeight}
                  textFill={textFill}
                  bgFillStyle={solidStrockeStyle}
                  bgStroke={solidStrockeStyle}
                  bgStrokeWidth={1}
                  selected={false}
                  showBackground={true}
                  paddingX={5}
                  paddingY={5}
                  usePixelCoordinates={true}
                />

                {renderArrowHead(dataVerticalStartXY, dataVerticalEndXY, dataArrowDirectionVert)}
                {renderArrowHead(dataHorizontalStartXY, dataHorizontalEndXY, dataArrowDirectionHorz)}
              </g>
            ));
          })}

        {/* Mouse interaction */}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          r={currentPositionRadius}
          stroke={strokeStyle}
          opacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleMouseDown as (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => void}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          onMouseMove={this.handleDrawSquare as (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => void}
          // onClick={this.handleZoomComplete}
        />
      </g>
    );
  }

  private handleMouseDown = (e: React.MouseEvent, _: unknown, moreProps: MoreProps) => {
    const { sharedState } = this.props;

    if (sharedState?.status !== 'started') {
      this.handleStartDrawing(e, moreProps);
    } else {
      this.HandleEnd(e, _, moreProps);
    }
  };

  private handleStartDrawing = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { setSharedState } = this.props;

    const {
      mouseXY: [, mouseY],
      currentItem,
      chartConfig: { yScale },
      xAccessor,
      xScale,
      chartId,
    } = moreProps;

    // Convert global mouseY to local chart Y
    const x = xAccessor(currentItem);
    const y = yScale.invert(mouseY);

    const x1y1 = [xScale(x), mouseY];

    const currentPosition = {
      start: { item: currentItem, xValue: x, yValue: y },
      end: null,
    };

    const sharedShape: MeasureSharedState = {
      startPosition: currentPosition.start,
      x1y1,
      end: null,
      status: 'started',
      chartId,
      type: ShapeType.MEASURE,
    };

    this.setState(
      {
        x1y1,
        start: currentPosition.start,
        rect: null,
      },
      () => {
        setSharedState?.(sharedShape);
        this.props.onStart?.(e, currentPosition, moreProps);
      },
    );
  };

  private HandleEnd = (e: React.MouseEvent, _: unknown, moreProps: MoreProps) => {
    const { sharedState, setSharedState, chartId, timeFrame } = this.props;
    if (!sharedState?.startPosition) return;
    const startPosition = sharedState.startPosition;
    const shapesChartId = sharedState.chartId ?? chartId;

    const metrics = computeMeasureMetrics(startPosition, chartId, shapesChartId, timeFrame ?? '', moreProps);

    const shapeUuid = crypto.randomUUID();

    // 완료된 Measure 도형의 모든 메트릭(rect, end, textPosition, vertical/horizontalStartXY,
    // arrowDirection 등)을 노드에 보존한다. render 경로의 `completedData = completedRect?.[0]`이
    // 이 필드들을 직접 읽어 Info 박스 좌표·화살표 위치를 표시하므로, 누락 시 (0,0)으로 fallback되어
    // 차트 좌상단에 잘못된 위치로 그려진다.
    const newBrush = [
      {
        ...metrics,
        start: startPosition,
        chartId: sharedState.chartId,
        selected: true,
        type: ShapeType.MEASURE,
        uuid: shapeUuid,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      },
    ];

    this.setState(
      {
        start: undefined,
        rect: null,
        x1y1: undefined,
        end: undefined,
        metrics: null,
      },
      () => {
        this.props.onBrush?.(e, newBrush, moreProps);

        setSharedState?.({
          ...sharedState,
          status: 'completed',
          x1y1: undefined,
          end: undefined,
        });
      },
    );
  };

  public handleZoomStart = (_: React.MouseEvent, __: unknown, moreProps: MoreProps) => {
    this.zoomHappening = false;
    const {
      mouseXY: [, mouseY],
      currentItem,
      chartConfig: { yScale },
      xAccessor,
      xScale,
    } = moreProps;

    const x1y1 = [xScale(xAccessor(currentItem)), mouseY];

    this.setState({
      x1y1,
      start: {
        item: currentItem,
        xValue: xAccessor(currentItem),
        yValue: yScale.invert(mouseY),
      },
      rect: null,
    });
  };

  public handleDrawSquare = (_: React.MouseEvent, _xyValue: [number, number], moreProps: MoreProps) => {
    const { sharedState, setSharedState, chartId, timeFrame } = this.props;
    if (!sharedState || sharedState.status !== 'started') return;

    const startChartId = sharedState.chartId;

    // Only draw for shapes created on this chart
    if (chartId !== startChartId) return;

    if (!sharedState.startPosition) return;

    this.zoomHappening = true;

    const shapesChartId = sharedState.chartId ?? chartId;
    const metrics = computeMeasureMetrics(
      sharedState.startPosition,
      chartId,
      shapesChartId,
      timeFrame ?? '',
      moreProps,
    );

    // Store only coordinate data in sharedState, derived render data in local state
    this.setState({ metrics });
    setSharedState?.((prevState: MeasureSharedState) => ({
      ...prevState,
      end: metrics.end,
    }));
  };
}
